import type { ArgsOf, RequestOf } from "../../../../access/types.ts";
import { Access, Delegate } from "../../../../access/types.ts";
import { CsaAccesses } from "../../csa/accesses.ts";
import { TailscaleAccesses } from "../../tailscale/accesses.ts";
import { ZpaAccesses } from "../../zpa/accesses.ts";
import { commonAllAccess } from "./all.ts";
import { commonGroupAccess } from "./group.ts";
import {
  groupNameParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";

export const UnknownSshAccesses = {
  session: Access({
    description: "SSH session instance",
    label: "SSH Session",
    maturity: "ga",
    // The SSH integration uses a single `UnknownSshAccesses` object passed to both
    // `commandsOf` and `LifecyclePermissioner`, covering all SSH providers. The lifecycle
    // framework resolves delegation at runtime via `accesses[key].delegation[to]`, so any
    // delegation key that any SSH provider's lifecycle may return from `use()` must be
    // declared here — not only on the provider-specific access (e.g. `SelfHostedSshAccess`),
    // which only serves TypeScript typing of the lifecycle.
    delegation: {
      "zscaler-application-segment": Delegate({
        resource: "zscaler-private-access",
        key: "application-segment",
        accesses: ZpaAccesses,
      }),
      "tailscale-ssh": Delegate({
        resource: "tailscale",
        key: "ssh",
        accesses: TailscaleAccesses,
      }),
      "cisco-resource": Delegate({
        resource: "cisco-secure-access",
        key: "resource",
        accesses: CsaAccesses,
      }),
    },
    generated: {},
    parameters: {
      // Because we can't limit options to just one command (p0 ls or p0 request) the --group option
      // also appears on p0 request but it does nothing. Return an undefined block because we don't
      // want to show this as an option in the Slack Bot UI.
      group: groupNameParameter,
      destination: sessionDestinationParameter,
      parent: parentParameter,
      provider: { ...requiredProviderParameter, optional: true },
      publicKey: publicKeyParameter,
      region: regionParameter,
      sudo: sudoParameter,
      zone: zoneParameter,
    },
    render: { locator: "destination", policy: "sudo" } as const,
  }),
  group: commonGroupAccess,
  all: commonAllAccess,
  parent: commonParentAccess,
};

export type UnknownSshAccesses = typeof UnknownSshAccesses;
export type UnknownSshAccess = UnknownSshAccesses[keyof UnknownSshAccesses];

export type UnknownSshSessionAccess = UnknownSshAccesses["session"];
export type UnknownSshGroupAccess = UnknownSshAccesses["group"];
export type UnknownSshParentAccess = UnknownSshAccesses["parent"];
export type UnknownSshAllAccess = UnknownSshAccesses["all"];

export type UnknownSshSessionRequest = RequestOf<UnknownSshSessionAccess>;
export type UnknownSshGroupRequest = RequestOf<UnknownSshGroupAccess>;
export type UnknownSshParentRequest = RequestOf<UnknownSshParentAccess>;
export type UnknownSshAllRequest = RequestOf<UnknownSshAllAccess>;
export type UnknownSshRequest = RequestOf<UnknownSshAccess>;

export type UnknownSshSessionGenerated = UnknownSshSessionAccess["generated"];
export type UnknownSshGroupGenerated = UnknownSshGroupAccess["generated"];
export type UnknownSshParentGenerated = UnknownSshParentAccess["generated"];
export type UnknownSshAllGenerated = UnknownSshAllAccess["generated"];

export type UnknownSshArgs = ArgsOf<UnknownSshAccess>;
export type UnknownSshSessionArgs = ArgsOf<UnknownSshSessionAccess>;
export type UnknownSshGroupArgs = ArgsOf<UnknownSshGroupAccess>;
export type UnknownSshParentArgs = ArgsOf<UnknownSshParentAccess>;
export type UnknownSshAllArgs = ArgsOf<UnknownSshAllAccess>;
